import React from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { MODULES, getModuleIcon, getModuleColors } from '../../content';
import { Badge } from '../../components/ui/Badge';
import { MasteryRing } from '../../portals/shared/MasteryRing';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { CardSurface } from '../shared/CardSurface';
import { MasteryProgressBar } from '../shared/MasteryProgressBar';
import { levelBadgeLabel } from './levelLabel';
import type { FamilyWithMastery } from './types';
interface QuickStartCardProps {
  item: FamilyWithMastery;
  onSelect: (familyId: string) => void;
  /** true = bovenaan alle vakken, false = bovenaan dit ene vak. */
  isQuickStart: boolean;
  learnerGroup: number;
}

/**
 * QuickStartCard — de snelle ingang bovenaan de kiezer.
 *
 * Heette `DjayPickCard` en presenteerde zich als "Djay's keuze". Het is het
 * eerste item van de gesorteerde lijst; er zit geen afweging achter. De naam en
 * het label claimden een aanbeveling die niet bestond, en dat is precies het
 * soort belofte dat een kind niet kan controleren. Zodra er echte selectielogica
 * achter zit, mag Djay zijn naam terugkrijgen.
 */
export function QuickStartCard({
  item,
  onSelect,
  isQuickStart,
  learnerGroup
}: QuickStartCardProps) {
  const {
    family,
    masteryScore,
    attempts
  } = item;
  const module = MODULES.find(m => m.id === family.module);
  const ModIcon = getModuleIcon(module?.icon || '');
  const colors = module ? getModuleColors(module.color) : null;
  const levelLabel = levelBadgeLabel(family.levelCode, learnerGroup);
  return <CardSurface initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} disableDefaultSound onClick={() => {
    playSoundIntent('reward.streak');
    onSelect(family.familyId);
  }}
  /* `border-3` bestaat niet in Tailwind: die klasse werd genegeerd, dus deze
     kaart had ondanks `border-amber-300` gewoon géén rand — terwijl de rand
     het enige is dat hem van de gewone kaarten onderscheidt. Nu border-2. */ className="w-full mb-4 p-4 rounded-3xl border-2 border-amber-300 bg-amber-50 hover:shadow-xl transition-shadow group overflow-hidden" data-id="element-3003" data-mp="1l659hzlx72j4">
      
      <header className="flex items-center gap-1.5 mb-3 w-full relative z-10" data-id="element-3004" data-mp="29lv3zmr6ufu7">
        <div className="bg-amber-200/60 text-amber-600 p-1 rounded-lg" data-id="element-3005" data-mp="25j8is4p0vt5z">
          <Sparkles className="w-3.5 h-3.5" data-id="element-3006" data-mp="1htvzud3w86qh" />
        </div>
        <span className="text-[11px] font-black text-amber-600 uppercase tracking-wider" data-id="element-3007" data-mp="1ctrkiqeq1i1u">
          {isQuickStart ? 'Snel starten' : 'Bovenaan voor dit vak'}
        </span>
      </header>

      <div className="flex items-center gap-3 relative z-10 w-full" data-id="element-3008" data-mp="1qr6du683i9z3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm ${colors?.text || 'text-slate-500'}`} data-id="element-3009" data-mp="1lswi9uigl7cy">
          
          <ModIcon className="w-6 h-6" data-id="element-3010" data-mp="2xaebw16bsqlr" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-3011" data-mp="2xbpe3wk4hqar">
          <h4 className="text-base font-black text-slate-900 leading-tight line-clamp-2" data-id="element-3012" data-mp="35q3smxbgmgdo">
            {family.description}
          </h4>
          <div className="flex items-center gap-2 mt-1" data-id="element-3013" data-mp="39ts2xnrh7q2m">
            {levelLabel && <Badge variant="warning" size="sm" data-id="element-3014" data-mp="2vthcglu2wdhz">
                {levelLabel}
              </Badge>}
            <span className="text-xs text-slate-500 font-medium truncate" data-id="element-3015" data-mp="30slkva96i779">
              {module?.title}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2" data-id="element-3016" data-mp="3ojh8rwaiki98">
          <MasteryRing score={masteryScore} size={44} data-id="element-3017" data-mp="3tfofhraqhxs8" />
          <ChevronRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 transition-transform" data-id="element-3018" data-mp="3erlbuok5yy6l" />
        </div>
      </div>

      <div className="mt-4 relative z-10 w-full" data-id="element-3019" data-mp="3jp3jmbmg37d0">
        <MasteryProgressBar masteryScore={masteryScore} caption={attempts > 0 ? `${attempts}× geoefend` : undefined} data-id="element-3020" data-mp="bal8g117jbp6" />
        
      </div>
    </CardSurface>;
}